// PromoCodeInput — promo code entry with Apply button.
// States: idle, validating, applied, invalid.
// Inline styles only — no Tailwind. Composed from embed-shared primitives.

import { useState, useCallback } from "react";
import {
  useEmbedTokens,
  EmbedField,
  EmbedInput,
  EmbedButton,
  EmbedBanner,
} from "../../embed-shared/primitives";
import { ApiError } from "../../embed-shared/api-client";
import { idempotencyUserMessage, isIdempotencyCodeBug } from "../../embed-shared/idempotency-errors";

type PromoState = "idle" | "validating" | "applied" | "invalid";

export interface PromoCodeInputProps {
  /** Called when the user clicks Apply. Must resolve or throw. */
  onApply: (code: string) => Promise<void>;
  /** Called when the user clears an applied promo. */
  onClear: () => Promise<void>;
  /** Currently applied promo code from cart state. */
  appliedCode?: string | null;
}

export function PromoCodeInput({ onApply, onClear, appliedCode }: PromoCodeInputProps) {
  const t = useEmbedTokens();
  const [code, setCode] = useState(appliedCode ?? "");
  const [state, setState] = useState<PromoState>(appliedCode ? "applied" : "idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleApply = useCallback(async () => {
    const trimmed = code.trim();
    if (!trimmed) return;
    setState("validating");
    setErrorMessage("");
    try {
      await onApply(trimmed);
      setState("applied");
    } catch (err) {
      setState("invalid");
      if (err instanceof ApiError) {
        if (isIdempotencyCodeBug(err.code)) {
          console.warn("[promo] missing Idempotency-Key — code bug", err.code);
        }
        setErrorMessage(idempotencyUserMessage(err.code) ?? err.message);
      } else {
        setErrorMessage("Invalid promo code");
      }
    }
  }, [code, onApply]);

  const handleClear = useCallback(async () => {
    try {
      await onClear();
    } catch {
      // Best-effort clear.
    }
    setCode("");
    setState("idle");
    setErrorMessage("");
  }, [onClear]);

  const applied = state === "applied";
  const validating = state === "validating";
  const invalid = state === "invalid";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.xs }}>
      <EmbedField label="Promo code" htmlFor="embed-promo-input">
        <div style={{ display: "flex", gap: t.spacing.sm }}>
          <div style={{ flex: 1 }}>
            <EmbedInput
              id="embed-promo-input"
              type="text"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                if (invalid) {
                  setState("idle");
                  setErrorMessage("");
                }
              }}
              disabled={applied || validating}
              placeholder="Enter code"
              invalid={invalid}
              aria-invalid={invalid}
              aria-describedby={invalid ? "embed-promo-error" : undefined}
            />
          </div>
          {applied ? (
            <EmbedButton type="button" variant="secondary" onClick={handleClear}>
              Clear
            </EmbedButton>
          ) : (
            <EmbedButton
              type="button"
              variant="primary"
              onClick={handleApply}
              disabled={validating || !code.trim()}
              loading={validating}
            >
              {validating ? "Checking..." : "Apply"}
            </EmbedButton>
          )}
        </div>
      </EmbedField>
      {invalid && errorMessage && (
        <span id="embed-promo-error">
          <EmbedBanner tone="error">{errorMessage}</EmbedBanner>
        </span>
      )}
      {applied && <EmbedBanner tone="success">Promo code applied</EmbedBanner>}
    </div>
  );
}
